/**
 * DETAIL LAYERS THAT NO ONE PHOTOGRAPHED.
 *
 * The three shipped detail maps are high-passed crops of reference art — a rock
 * face, a plating scan, a coral crust — and they are good at what they are. What
 * they are not is a MATERIAL SET: every hard surface in the game, painted hull
 * and machined fitting alike, was reaching for the same plating crop, so a
 * lacquered flank and a steel bracket differed in tint and in nothing else.
 *
 * These two fill that gap, and they are generated rather than drawn for the same
 * reason `studioMap.ts` and `bake-env.mjs` are: what is wanted is a small set of
 * exact, seamlessly tiling, lighting-free fields, and those are easier to
 * compute than to photograph. A crop of a real brushed panel arrives with a
 * highlight baked across it and a seam down one side.
 *
 * OUTPUT IS A HEIGHT FIELD, NOT A FINISHED LAYER. Each returns greyscale RGBA
 * for `buildLayer`, which high-passes it, takes a Sobel normal and derives the
 * cavity — the identical path the scanned art takes. So a generated layer and a
 * photographed one are the same kind of object downstream, and anything that
 * improves the packing improves both.
 *
 * EVERYTHING HERE MUST TILE. The mesh path projects these triplanar across
 * whole habitat modules and seabeds, so a discontinuity is not a seam on one
 * face — it is a line ruled across the entire world at a fixed interval. Every
 * lattice below wraps on its own period, which is why the noise takes explicit
 * periods rather than a frequency.
 */

const SIZE = 256;

/**
 * Integer hash on a WRAPPED lattice.
 *
 * `px`/`py` are the periods, and the wrap happens here rather than at the call
 * site so that no caller can forget it — forgetting it is exactly the bug that
 * produces a visible grid across the seabed, and it does not show up in a
 * preview of one tile.
 */
function hash2(x: number, y: number, px: number, py: number, seed: number): number {
  const ix = ((x % px) + px) % px;
  const iy = ((y % py) + py) % py;
  let h = Math.imul(ix, 374761393) ^ Math.imul(iy, 668265263) ^ Math.imul(seed, 2246822519);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Hermite, so the lattice does not show as diamonds. */
const fade = (t: number) => t * t * (3 - 2 * t);

/**
 * Value noise with INDEPENDENT periods on each axis.
 *
 * The two periods are what make a brush grain possible at all: a small period on
 * one axis and a large one on the other gives a field that barely changes along
 * the first and changes fast across it, which is a streak running down the
 * first axis. One frequency cannot express that, and rotating an isotropic
 * noise cannot either — it would stop tiling.
 */
function vnoise(u: number, v: number, px: number, py: number, seed: number): number {
  const x = u * px, y = v * py;
  const x0 = Math.floor(x), y0 = Math.floor(y);
  const fx = fade(x - x0), fy = fade(y - y0);
  const a = hash2(x0, y0, px, py, seed);
  const b = hash2(x0 + 1, y0, px, py, seed);
  const c = hash2(x0, y0 + 1, px, py, seed);
  const d = hash2(x0 + 1, y0 + 1, px, py, seed);
  const top = a + (b - a) * fx;
  const bot = c + (d - c) * fx;
  return top + (bot - top) * fy;
}

const smoothstep = (e0: number, e1: number, x: number) => {
  const t = Math.max(0, Math.min(1, (x - e0) / Math.max(1e-6, e1 - e0)));
  return t * t * (3 - 2 * t);
};

/** Pack a height field in 0..1 into the greyscale RGBA `buildLayer` expects. */
function pack(height: (u: number, v: number) => number): Uint8ClampedArray {
  const px = new Uint8ClampedArray(SIZE * SIZE * 4);
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const h = Math.max(0, Math.min(1, height(x / SIZE, y / SIZE)));
      const o = (y * SIZE + x) * 4;
      const v = Math.round(h * 255);
      px[o] = v; px[o + 1] = v; px[o + 2] = v;
      px[o + 3] = 255;
    }
  }
  return px;
}

/**
 * BRUSHED METAL. Directional grain, and the direction is the whole material.
 *
 * A conductor with isotropic roughness is a mirror or a grey ball and nothing
 * in between. What says "machined" is that the microfacets share an axis, so
 * the highlight smears ALONG it and stays tight across it. The shader already
 * has a lobe for this — `iblBrushed` smears the environment along world X — but
 * it was working off a plating crop with no grain in it, which is a smear
 * applied to a surface that never said it had a direction.
 *
 * THE GRAIN RUNS ALONG U, WHICH MEANS THE PERIODS LOOK BACKWARDS.
 *
 * A scratch parallel to u is one whose value is constant AS u CHANGES and
 * varies as v does — so the small period goes on u and the large one on v. The
 * obvious spelling gives 256 cells across u, which varies fastest along the
 * axis the scratch is supposed to run down, and produces a grain at ninety
 * degrees to the one intended.
 *
 * u has to be the axis because `iblBrushed` smears the environment along world
 * X, and in the triplanar Z projection — the one facing this camera — u IS
 * world x. The grain and the lobe that exploits it have to agree or the smear
 * is applied across the scratches instead of along them.
 *
 * Four octaves. The two fine ones carry the tool marks; the 4/128 pair groups
 * them into bands so the surface is not a uniform hiss; the last is nearly
 * isotropic and does what a clear coat does, which is stop the grain reading as
 * corduroy.
 */
export function brushedMetal(): Uint8ClampedArray {
  return pack((u, v) => {
    let h = 0.5;
    h += (vnoise(u, v, 6, 256, 11) - 0.5) * 0.40;
    h += (vnoise(u, v, 12, 512, 12) - 0.5) * 0.24;
    h += (vnoise(u, v, 4, 128, 13) - 0.5) * 0.16;
    h += (vnoise(u, v, 32, 32, 14) - 0.5) * 0.10;
    return h;
  });
}

/**
 * PAINTED PLATE. Panels, and the seams between them.
 *
 * The thing that makes a built object read as built is that it is ASSEMBLED —
 * finite pieces, bolted up, with a line where two of them meet. One continuous
 * field of grain across a sixteen-metre module says "extruded", which is what
 * the habitat has been saying.
 *
 * Four panels each way, so at the mesh path's 0.42 world scale a panel lands
 * around 1.5 m — roughly a real plate, and small enough that a module shows
 * several without the texture repeating visibly.
 *
 * NO RIVETS. The boat generates its own procedurally in `fsMesh`, sized for a
 * 7.2 m hull, and putting a second set in a tiling map would collide with them
 * at a different scale on the one asset that has both. Seams and wear only.
 */
export function paintedPlate(): Uint8ClampedArray {
  const PANELS = 4;
  return pack((u, v) => {
    const gx = u * PANELS, gy = v * PANELS;
    const cx = Math.floor(gx), cy = Math.floor(gy);
    const fx = gx - cx, fy = gy - cy;
    // Distance to the nearest panel edge, in panel units.
    const dEdge = Math.min(Math.min(fx, 1 - fx), Math.min(fy, 1 - fy));

    let h = 0.5;
    // The seam itself: a narrow groove. Sharp, because a shut line is sharp and
    // the high-pass downstream will only keep what is sharp anyway.
    h -= (1 - smoothstep(0.0, 0.030, dEdge)) * 0.34;
    // And the lip either side of it, where the plate is pressed and stands
    // slightly proud. This is what stops the seam reading as a drawn line.
    h += (smoothstep(0.030, 0.060, dEdge) - smoothstep(0.060, 0.115, dEdge)) * 0.10;
    // Each plate sits a hair differently. Below the high-pass cutoff, so it
    // survives as a faint tonal step at the seam rather than as a checkerboard.
    h += (hash2(cx, cy, PANELS, PANELS, 21) - 0.5) * 0.06;
    // Orange peel — the texture a sprayed finish actually has.
    h += (vnoise(u, v, 192, 192, 22) - 0.5) * 0.11;
    h += (vnoise(u, v, 72, 72, 23) - 0.5) * 0.07;
    // Wear gathers along the seams, where a plate gets knocked and handled.
    h += (vnoise(u, v, 160, 160, 24) - 0.5) * 0.16 * (1 - smoothstep(0.0, 0.22, dEdge));
    return h;
  });
}
